import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, MapPin, Clock, Facebook, Twitter, Linkedin, Instagram, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Footer = () => {
    const tawkContainerRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (!document.getElementById('tawk-style-hide')) {
            const style = document.createElement('style');
            style.id = 'tawk-style-hide';
            style.textContent = `
                .tawk-min-container,
                .tawk-button,
                iframe[title="chat widget"],
                iframe[title="chat widget"] + div {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                }
            `;
            document.head.appendChild(style);
        }

        const existingScript = document.querySelector(
            'script[src="https://embed.tawk.to/6989cb30585bab1c339ee95b/1jh143dnj"]'
        );
        if (existingScript) return;

        window.Tawk_API = window.Tawk_API || {};
        window.Tawk_API.onLoad = function () {
            if (window.Tawk_API?.hideWidget) {
                window.Tawk_API.hideWidget();
            }
        };
        window.Tawk_LoadStart = new Date();

        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://embed.tawk.to/6989cb30585bab1c339ee95b/1jh143dnj';
        script.charset = 'UTF-8';
        script.setAttribute('crossorigin', '*');

        const target = tawkContainerRef.current || document.body;
        target.appendChild(script);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const openTawkChat = () => {
        if (window.Tawk_API?.maximize) {
            window.Tawk_API.maximize();
        }
    };

    const quickLinks = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Our Services', path: '/services' },
        { name: 'Training', path: '/training' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Clients', path: '/clients' },
        { name: 'Careers', path: '/careers' },
        { name: 'Contact', path: '/contact' },
    ];

    const services = [
        'Armed Guards',
        'Corporate Security',
        'VIP Protection',
        'Event Security',
        'Industrial Security',
        'Security Training',
    ];

    const contactInfo = [
        { 
            icon: MapPin, 
            text: '#53/3C, Ground Floor, 20th Cross Road, Raghuvendra Layout, Hulimavu, Bannerghatta Road, Bangalore-560076',
            href: 'https://maps.google.com/?q=12.879273,77.603355'
        },
        { 
            icon: Phone, 
            text: '+91-9606924394',
            href: 'tel:+919606924394'
        },
        { 
            icon: Mail, 
            text: 'enquiry@shivasecurity.com',
            href: 'mailto:enquiry@shivasecurity.com'
        },
        {
            icon: Clock,
            text: 'Working Hour - Monday to Saturday 10:00 AM to 6:00 PM',
            href: null
        },
    ];

    const socialLinks = [
        { icon: Facebook, href: '#', label: 'Facebook' },
        { icon: Twitter, href: '#', label: 'Twitter' },
        { icon: Linkedin, href: '#', label: 'LinkedIn' },
        { icon: Instagram, href: '#', label: 'Instagram' },
    ];

    return (
        <footer className="relative bg-card border-t border-border">
            {/* Top Section with CTA */}
            <div className="container mx-auto px-4 lg:px-8">
                <div className="relative -mt-16 mb-16">
                    <div className="bg-hero-pattern rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6">
                        <div className="text-center lg:text-left">
                            <h3 className="font-display text-3xl lg:text-4xl text-primary-foreground mb-2">
                                Ready to Secure Your Assets?
                            </h3>
                            <p className="text-primary-foreground/80">
                                Reliable Security Solutions You Can Trust in Bangalore.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/contact">
                                <Button className="btn-accent rounded-lg px-8 py-6 text-lg font-semibold whitespace-nowrap">
                                    Get Free Quote
                                </Button>
                            </Link>
                            <a href="tel:+919606924394">
                                <Button 
                                    variant="outline" 
                                    className="rounded-lg px-8 py-6 text-lg font-semibold border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 whitespace-nowrap"
                                >
                                    <Phone className="w-5 h-5 mr-2" />
                                    Call Now
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Main Footer Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12">
                    {/* Brand Column */}
                    <div className="lg:col-span-1">
                        <Link to="/" className="flex items-center gap-3 mb-6">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_securityweb-2/artifacts/pwqd4vsv_logo.png" 
                                alt="Shiva Security Services"
                                className="w-14 h-14 object-contain"
                            />
                            <div className="flex flex-col items-center">
                                <span className="font-display font-extrabold text-2xl text-orange-600 dark:text-orange-500">SHIVA</span>
                                <span className="text-[9px] font-bold text-orange-500/80 dark:text-orange-400/80 tracking-[0.2em] -mt-0.5">
                                    SECURITY SERVICES
                                </span>
                            </div>
                        </Link>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                            Leader in providing comprehensive security solutions across Bangalore. 
                            Committed to excellence and client safety.
                        </p>
                        <div className="flex items-center gap-3">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.label}
                                    href={social.href}
                                    aria-label={social.label}
                                    className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-300"
                                >
                                    <social.icon className="w-5 h-5" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-display text-xl text-foreground mb-6">Quick Links</h4>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.path}>
                                    <Link 
                                        to={link.path}
                                        className="text-muted-foreground hover:text-accent transition-colors duration-300 text-sm flex items-center gap-2"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services */}
                    <div>
                        <h4 className="font-display text-xl text-foreground mb-6">Our Services</h4>
                        <ul className="space-y-3">
                            {services.map((service) => (
                                <li key={service}>
                                    <Link 
                                        to="/services"
                                        className="text-muted-foreground hover:text-accent transition-colors duration-300 text-sm flex items-center gap-2"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                                        {service}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="font-display text-xl text-foreground mb-6">Contact Us</h4>
                        <ul className="space-y-4">
                            {contactInfo.map((info, index) => (
                                <li key={index}>
                                    {info.href ? (
                                        <a 
                                            href={info.href}
                                            className="flex items-start gap-3 text-muted-foreground hover:text-accent transition-colors duration-300 group"
                                        >
                                            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-accent/10">
                                                <info.icon className="w-5 h-5" />
                                            </div>
                                            <span className="text-sm leading-relaxed pt-2">{info.text}</span>
                                        </a>
                                    ) : (
                                        <div className="flex items-start gap-3 text-muted-foreground">
                                            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                                <info.icon className="w-5 h-5" />
                                            </div>
                                            <span className="text-sm leading-relaxed pt-2">{info.text}</span>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-border py-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-muted-foreground text-center md:text-left">
                        <p>© {new Date().getFullYear()} Shiva Security Services. All rights reserved.</p>
                        <p className="text-xs mt-1">GSTIN: 29CSWP8056Q2Z8</p>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <a href="#" className="hover:text-accent transition-colors">Privacy Policy</a>
                        <a href="#" className="hover:text-accent transition-colors">Terms of Service</a>
                    </div>
                </div>
            </div>

            {/* Tawk.to Widget */}
            <div ref={tawkContainerRef} />

            {/* Custom Tawk Chat Button */}
            <button
                onClick={openTawkChat}
                className="fixed bottom-8 right-4 w-16 h-16 md:w-20 md:h-20 rounded-full bg-transparent flex items-center justify-center hover:scale-105 transition-transform duration-300 z-[9999]"
                aria-label="Open chat"
                data-testid="open-chat"
            >
                <img src="/images/chat.png" alt="Chat with Shiva Security Services" className="w-12 h-12 md:w-16 md:h-16 object-contain" />
            </button>

            {/* Scroll to Top Button */}
            <button
                onClick={scrollToTop}
                className="fixed bottom-28 right-7 w-12 h-12 rounded-full btn-accent flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 z-[9999]"
                aria-label="Scroll to top"
                data-testid="scroll-to-top"
            >
                <ArrowUp className="w-5 h-5" />
            </button>
        </footer>
    );
};
